import { useEffect, useRef, useState, useCallback } from 'react';

export function useWebSocket(url, onMessage) {
  const ws = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const messageQueueRef = useRef([]);
  const onMessageRef = useRef(onMessage);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const reconnectDelayRef = useRef(1000); // Start with 1 second, will increase

  // Keep onMessage ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Send queued messages when connection is restored
  const flushMessageQueue = useCallback(() => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN && messageQueueRef.current.length > 0) {
      console.log(`📤 Flushing ${messageQueueRef.current.length} queued messages`);
      const queue = [...messageQueueRef.current];
      messageQueueRef.current = [];
      queue.forEach(msg => {
        try {
          ws.current.send(JSON.stringify(msg));
        } catch (error) {
          console.error('Error sending queued message:', error);
          // Re-queue if send fails
          messageQueueRef.current.push(msg);
        }
      });
    }
  }, []);

  // Heartbeat to keep connection alive and detect stale connections
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    heartbeatIntervalRef.current = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        try {
          ws.current.send(JSON.stringify({ type: 'ping' }));
        } catch (error) {
          console.error('Error sending heartbeat:', error);
          setConnected(false);
        }
      }
    }, 30000); // Send ping every 30 seconds
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const connect = () => {
      try {
        const wsUrl = url.startsWith('ws://') || url.startsWith('wss://') 
          ? url 
          : (process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host);
        console.log(`[WS] Attempting to connect to WebSocket: ${wsUrl} (attempt ${reconnectAttemptsRef.current + 1})`);
        
        // Close existing connection if any
        if (ws.current) {
          try {
            ws.current.onclose = null; // Prevent reconnection trigger
            ws.current.close();
          } catch (e) {
            // Ignore
          }
        }
        
        ws.current = new WebSocket(wsUrl);
        console.log('[WS] WebSocket created, initial state:', ws.current.readyState);

        ws.current.onopen = () => {
          console.log('[WS] ✅ WebSocket connected!');
          setConnected(true);
          reconnectAttemptsRef.current = 0;
          reconnectDelayRef.current = 1000; // Reset delay
          startHeartbeat();
          flushMessageQueue(); // Send any queued messages
        };

        ws.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            // Handle pong response
            if (data.type === 'pong') {
              // Connection is alive, do nothing
              return;
            }
            
            console.log('[WS] 📨 Message received:', data.type, data);
            if (data.type === 'voteTie' || data.type === 'voteResult') {
              console.log('[WS] 🚨 ANNOUNCEMENT MESSAGE:', data.type, JSON.stringify(data));
            }
            if (data.type === 'connected') {
              console.log('[WS] ✅ Server confirmed connection:', data.message);
              setConnected(true);
            }
            if (onMessageRef.current) {
              try {
                onMessageRef.current(data);
              } catch (error) {
                console.error('[WS] Error in message handler:', error);
              }
            }
          } catch (error) {
            console.error('[WS] Error parsing message:', error);
          }
        };

        ws.current.onerror = (error) => {
          console.error('[WS] ❌ WebSocket error:', error);
          setConnected(false);
        };

        ws.current.onclose = (event) => {
          setConnected(false);
          stopHeartbeat();
          console.log('[WS] 🔌 Disconnected - Code:', event.code, 'Reason:', event.reason || 'No reason');
          
          // Only reconnect if it's an unexpected closure
          if (event.code !== 1000 && event.code !== 1001 && !ws.current?._intentionalClose) {
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
              reconnectAttemptsRef.current++;
              const delay = Math.min(reconnectDelayRef.current * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
              console.log(`[WS] Will reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
              reconnectTimeoutRef.current = setTimeout(() => {
                console.log('[WS] 🔄 Reconnecting...');
                connect();
              }, delay);
            } else {
              console.error('[WS] Max reconnection attempts reached. Please refresh the page.');
            }
          } else {
            console.log('[WS] Closed intentionally, not reconnecting');
          }
        };
      } catch (error) {
        console.error('[WS] ❌ Error creating WebSocket:', error);
        setConnected(false);
        // Retry connection
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelayRef.current * reconnectAttemptsRef.current);
        }
      }
    };

    connect();

    return () => {
      stopHeartbeat();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (ws.current) {
        try {
          ws.current._intentionalClose = true;
          ws.current.onclose = null; // Prevent reconnection
          ws.current.close(1000, 'Component unmounting');
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [url, startHeartbeat, stopHeartbeat, flushMessageQueue]);

  const send = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      try {
        ws.current.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('[WS] Error sending message:', error);
        // Queue message for retry
        messageQueueRef.current.push(message);
        return false;
      }
    } else {
      console.warn('[WS] Connection not open, queuing message. State:', ws.current?.readyState);
      // Queue message to send when connection is restored
      messageQueueRef.current.push(message);
      return false;
    }
  }, []);

  return { send, connected, ws: ws.current };
}
